import React, { useState, useEffect } from 'react';
import { ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from 'recharts';
import { gamesAPI } from '../api/games';
import { Calendar, Gamepad2, Search, Loader2, Check, Trash2, Link, X, ExternalLink, Youtube, Eye, Download, Users, MessageSquare, Megaphone, AlertTriangle } from 'lucide-react';
import { isYouTubeDataStale } from '../utils/youtubeCompliance';

const Games = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [enrichingGameId, setEnrichingGameId] = useState(null);
  const [searchingIGDB, setSearchingIGDB] = useState(false);
  const [igdbFound, setIgdbFound] = useState(false);
  const [linkingContent, setLinkingContent] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameContent, setGameContent] = useState([]);
  const [loadingContent, setLoadingContent] = useState(false);
  const [linkingSingleGame, setLinkingSingleGame] = useState(false);
  const [modalTab, setModalTab] = useState('content');
  const [bdSort, setBdSort] = useState({ key: 'watchHours', dir: 'desc' });
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [bdExpanded, setBdExpanded] = useState(new Set());
  const [bdExporting, setBdExporting] = useState(false);
  const [bdRange, setBdRange] = useState('all');
  const [bdPlatform, setBdPlatform] = useState('twitch'); // Stream Breakdown source: 'twitch' | 'kick'
  const [bdCustom, setBdCustom] = useState({ from: '', to: '' });
  const [bdLangs, setBdLangs] = useState(new Set());
  const [bdLangOpen, setBdLangOpen] = useState(false);
  // Video Breakdown (YouTube uploads) — rolling 30-day window
  const [vc, setVc] = useState(null);
  const [vcLoading, setVcLoading] = useState(false);
  const [vcRange, setVcRange] = useState('30');
  const [vcSort, setVcSort] = useState({ key: 'views', dir: 'desc' });
  const [vcLangs, setVcLangs] = useState(new Set());
  const [vcLangOpen, setVcLangOpen] = useState(false);
  // Steam Signals — recorded player counts, review movement, official updates
  const [steam, setSteam] = useState(null);
  const [steamLoading, setSteamLoading] = useState(false);
  const [steamError, setSteamError] = useState(null);
  const [steamRange, setSteamRange] = useState('30');
  // Console Data — Switch/Xbox store signals. Its own tab and its own endpoint:
  // neither console publishes player counts, so it is store data only and does
  // not belong under Steam.
  const [consoleData, setConsoleData] = useState(null);
  const [consoleLoading, setConsoleLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    publisher: '',
    releaseDate: '',
    genres: '',
    platforms: '',
    coverImageUrl: '',
    igdbId: ''
  });

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await gamesAPI.getAll({ search: searchTerm, limit: 200 });
      setGames(response.data.games);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const gameData = {
        title: formData.title,
        publisher: formData.publisher || null,
        releaseDate: formData.releaseDate ? new Date(formData.releaseDate).toISOString() : null,
        genres: formData.genres.split(',').map(g => g.trim()).filter(g => g),
        platforms: formData.platforms.split(',').map(p => p.trim()).filter(p => p),
        coverImageUrl: formData.coverImageUrl || null,
        verified: !!formData.igdbId // Mark as verified if from IGDB
      };
      if (formData.igdbId) {
        gameData.igdbId = formData.igdbId;
      }
      await gamesAPI.create(gameData);
      fetchGames();
      setShowAddForm(false);
      setFormData({ title: '', publisher: '', releaseDate: '', genres: '', platforms: '', coverImageUrl: '', igdbId: '' });
      setIgdbFound(false);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to add game';
      alert(errorMsg);
    }
  };

  const handleEnrichGame = async (gameId) => {
    setEnrichingGameId(gameId);
    try {
      const response = await gamesAPI.enrichGame(gameId);
      alert('Game metadata updated successfully!');
      fetchGames();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to enrich game data';
      alert(errorMsg);
    } finally {
      setEnrichingGameId(null);
    }
  };

  const handleSearchIGDB = async () => {
    if (!formData.title || formData.title.trim().length < 2) {
      alert('Enter at least 2 characters to search');
      return;
    }

    setSearchingIGDB(true);
    setIgdbFound(false);
    try {
      const response = await gamesAPI.searchIGDB(formData.title.trim());
      if (response.data.found && response.data.game) {
        const game = response.data.game;
        setFormData({
          title: game.title || formData.title,
          publisher: game.publisher || '',
          releaseDate: game.releaseDate || '',
          genres: game.genres?.join(', ') || '',
          platforms: game.platforms?.join(', ') || '',
          coverImageUrl: game.coverImageUrl || '',
          igdbId: game.igdbId || ''
        });
        setIgdbFound(true);
      } else {
        alert('No game found on IGDB. You can still add it manually.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to search IGDB';
      alert(errorMsg);
    } finally {
      setSearchingIGDB(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short'
      });
    } catch {
      return null;
    }
  };

  const handleDeleteGame = async (gameId, gameTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${gameTitle}"?`)) {
      return;
    }
    try {
      await gamesAPI.delete(gameId);
      fetchGames();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to delete game';
      alert(errorMsg);
    }
  };

  const handleBulkLinkContent = async () => {
    setLinkingContent(true);
    try {
      const response = await gamesAPI.bulkLinkContent();
      const { totalLinked, details } = response.data;
      if (totalLinked > 0) {
        alert(`Linked ${totalLinked} content items!\n\n${details.map(d => `${d.game}: ${d.linked}`).join('\n')}`);
        fetchGames();
      } else {
        alert('No new content found to link. All content may already be linked.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to link content';
      alert(errorMsg);
    } finally {
      setLinkingContent(false);
    }
  };

  const handleGameClick = async (game) => {
    setSelectedGame(game);
    setModalTab('content');
    setLoadingContent(true);
    try {
      const response = await gamesAPI.getOne(game.id);
      setGameContent(response.data.game.content || []);
    } catch (err) {
      console.error('Failed to load game content:', err);
      setGameContent([]);
    } finally {
      setLoadingContent(false);
    }
  };

  const closeModal = () => {
    setSelectedGame(null);
    setGameContent([]);
    setModalTab('content');
    setCoverage(null);
  };

  // Fetch real stream-coverage data when the Breakdown tab is opened.
  useEffect(() => {
    if (!selectedGame || modalTab !== 'breakdown') return;
    let cancelled = false;
    const params = {};
    if (bdRange === 'custom') {
      if (bdCustom.from) params.from = bdCustom.from;
      if (bdCustom.to) params.to = bdCustom.to;
    } else if (bdRange === 'all') {
      // "All" overlays the most complete import we have (largest window).
      params.window = 'all';
    } else {
      params.from = new Date(Date.now() - parseInt(bdRange) * 86400000).toISOString();
      // Tell the route which preset window this is, so it can overlay a matching
      // SullyGnome import (30/60/90/180/365) for the same timeframe.
      params.window = bdRange;
    }
    setCoverageLoading(true);
    const fetchCoverage = bdPlatform === 'kick' ? gamesAPI.getKickCoverage : gamesAPI.getCoverage;
    fetchCoverage(selectedGame.id, params)
      .then((r) => { if (!cancelled) setCoverage(r.data); })
      .catch(() => { if (!cancelled) setCoverage({ tracked: false, streamers: [], totals: null, regions: [] }); })
      .finally(() => { if (!cancelled) setCoverageLoading(false); });
    return () => { cancelled = true; };
  }, [selectedGame, modalTab, bdRange, bdCustom, bdPlatform]);

  // Fetch YouTube video-coverage when the Video Breakdown tab is opened.
  useEffect(() => {
    if (!selectedGame || modalTab !== 'videos') return;
    let cancelled = false;
    setVcLoading(true);
    gamesAPI.getVideoCoverage(selectedGame.id, { window: vcRange })
      .then((r) => { if (!cancelled) setVc(r.data); })
      .catch(() => { if (!cancelled) setVc({ creators: [], totals: null }); })
      .finally(() => { if (!cancelled) setVcLoading(false); });
    return () => { cancelled = true; };
  }, [selectedGame, modalTab, vcRange]);

  // Fetch Steam data when the Steam tab is opened.
  useEffect(() => {
    if (!selectedGame || modalTab !== 'steam') return;
    let cancelled = false;
    setSteamLoading(true);
    setSteamError(null);
    gamesAPI.getSteam(selectedGame.id, { window: steamRange })
      .then((r) => { if (!cancelled) setSteam(r.data); })
      // Never fall back to "not linked" here. A failed request and an unlinked
      // game are different states, and showing the unlinked message for a 404
      // tells the user something false about their own data.
      .catch((e) => {
        if (cancelled) return;
        setSteam(null);
        setSteamError(
          e?.response?.status === 404
            ? 'The Steam endpoint isn’t available on the running server — it needs a restart to pick up the new route.'
            : e?.response?.data?.error || e?.message || 'Could not reach the server.'
        );
      })
      .finally(() => { if (!cancelled) setSteamLoading(false); });
    return () => { cancelled = true; };
  }, [selectedGame, modalTab, steamRange]);

  // Derived Steam view-model. Days with no readings are kept out of the chart
  // rather than plotted as zero — a gap in recording is not "nobody playing".
  const steamDaily = steam?.daily || [];
  const steamToday = steamDaily.length
    ? steamDaily.find((d) => d.date === new Date().toISOString().slice(0, 10)) || null
    : null;
  const steamFmtDay = (iso) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  // A day earns a place on the chart if ANY source has something for it. Creator
  // coverage usually reaches further back than the Steam recording, and cropping
  // to Steam's history would throw away exactly the coverage this view exists to
  // show. peak stays null on days with no Steam reading, so the area gaps rather
  // than dropping to zero — a gap in recording is not "nobody playing".
  const steamChart = (steam?.series || [])
    .filter((d) => d.peakCcu != null || d.watchHours > 0 || d.views > 0 || d.streams > 0)
    .map((d) => ({
      label: steamFmtDay(d.date),
      date: d.date,
      peak: d.peakCcu,
      // null, not 0 — a day with no coverage should draw no bar rather than a
      // zero-height stub sitting on the axis.
      watchHours: d.watchHours > 0 ? d.watchHours : null,
      views: d.views > 0 ? d.views : null,
      streams: d.streams || 0,
      videos: d.videos || 0,
    }));
  const steamHasCoverage = steamChart.some((d) => d.watchHours || d.views);
  // Window-aware figures. "Playing now" is a live reading and stays fixed, but a
  // peak and a review score should answer the question the timeframe asked —
  // showing today's peak and an all-time score while 30d is selected is a
  // non-answer.
  const steamWindowPeak = (steam?.series || []).reduce(
    (best, d) => (d.peakCcu != null && (!best || d.peakCcu > best.peakCcu) ? d : best), null);
  const steamWindowReviews = (steam?.daily || []).reduce((acc, d) => {
    if (d.reviewsAdded != null) acc.added += d.reviewsAdded;
    if (d.positiveAdded != null) acc.positive += d.positiveAdded;
    return acc;
  }, { added: 0, positive: 0 });
  const steamWindowLabel = steamRange === '7' ? '7 days'
    : steamRange === '30' ? '30 days' : steamRange === '90' ? '90 days' : '365 days';

  // Bought-on-Steam vs key-recipient reviews, moved into the selected window.
  // Both columns are running lifetime totals, so the window's movement is the
  // end-of-window total minus the reading immediately before the window opened.
  // When we have no reading from before the window, the window predates our
  // recording: we fall back to the lifetime split and label it as such rather
  // than presenting a partial delta as if it were the whole period.
  const steamWindowSplit = (() => {
    const rows = (steam?.daily || []).filter((d) => d.steamPurchaseReviews != null);
    const last = rows[rows.length - 1];
    if (!last) return null;
    // Prefer the true baseline. Failing that, the earliest split we hold inside
    // the window still gives real movement — it just covers less than the window
    // asked for, so it is labelled with the date it actually starts from.
    const base = steam?.baselineDaily || (rows.length > 1 ? rows[0] : null);
    if (!base) return null;
    const bought = (last.steamPurchaseReviews || 0) - (base.steamPurchaseReviews || 0);
    const keys = (last.keyReviews || 0) - (base.keyReviews || 0);
    const total = bought + keys;
    if (total <= 0) return null;
    return {
      bought, keys, total,
      pct: Math.round((keys / total) * 100),
      label: steam?.baselineDaily
        ? `in ${steamWindowLabel}`
        : `since ${new Date(`${base.date}T00:00:00Z`).toLocaleDateString()}`,
    };
  })();
  // How many days actually have a player reading. A line needs two points to
  // draw, so with one (or none) the series is invisible and the key would be
  // promising something that isn't on the chart.
  const steamPlayerDays = steamChart.filter((d) => d.peak != null).length;
  // Only mark updates that fall on a day we actually plotted, so a reference
  // line can't point at an x-value the axis doesn't have.
  const steamNewsMarks = (steam?.news || [])
    .map((n) => ({ gid: n.gid, date: n.publishedAt.slice(0, 10) }))
    .filter((n) => steamChart.some((c) => c.date === n.date))
    .map((n) => ({ ...n, label: steamFmtDay(n.date) }));

  // Fetch console data when the Console Data tab is opened.
  useEffect(() => {
    if (!selectedGame || modalTab !== 'console') return;
    let cancelled = false;
    setConsoleLoading(true);
    gamesAPI.getConsole(selectedGame.id)
      .then((r) => { if (!cancelled) setConsoleData(r.data); })
      .catch(() => { if (!cancelled) setConsoleData(null); })
      .finally(() => { if (!cancelled) setConsoleLoading(false); });
    return () => { cancelled = true; };
  }, [selectedGame, modalTab]);

  const vcCreators = vc?.creators || [];
  const vcAllRegions = [...new Set(vcCreators.map((c) => c.region))].sort();
  const vcFiltered = vcLangs.size ? vcCreators.filter((c) => vcLangs.has(c.region)) : vcCreators;
  const vcSorted = [...vcFiltered].sort((a, b) => {
    const r = (vcSort.key === 'region') ? String(a.region).localeCompare(String(b.region)) : (a[vcSort.key] - b[vcSort.key]);
    return vcSort.dir === 'asc' ? r : -r;
  });
  const toggleVcLang = (lang) => setVcLangs((s) => { const n = new Set(s); n.has(lang) ? n.delete(lang) : n.add(lang); return n; });
  const toggleVcSort = (k) => setVcSort((s) => s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'desc' });
  const vcArrow = (k) => vcSort.key === k ? (vcSort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  // ── Stream-coverage breakdown (real data from GET /games/:id/coverage) ──
  const bdData = coverage?.streamers || [];
  const sortedBd = [...bdData].sort((a, b) => {
    const k = bdSort.key;
    const r = (k === 'region') ? String(a.region).localeCompare(String(b.region)) : (a[k] - b[k]);
    return bdSort.dir === 'asc' ? r : -r;
  });
  const toggleBdSort = (k) => setBdSort((s) => s.key === k
    ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key: k, dir: k === 'region' ? 'asc' : 'desc' });
  const bdTotals = coverage?.totals || { streamers: 0, hoursStreamed: 0, watchHours: 0, peak: 0 };
  const bdRegions = coverage?.regions || [];
  const bdArrow = (k) => bdSort.key === k ? (bdSort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  const fmtDay = (d) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const toggleExpand = (name) => setBdExpanded((s) => { const n = new Set(s); n.has(name) ? n.delete(name) : n.add(name); return n; });
  const toggleLang = (lang) => setBdLangs((s) => { const n = new Set(s); n.has(lang) ? n.delete(lang) : n.add(lang); return n; });
  // Language filter (client-side): when languages are selected, restrict the
  // table + totals to those regions.
  const displayBd = bdLangs.size ? sortedBd.filter((r) => bdLangs.has(r.region)) : sortedBd;
  const displayTotals = bdLangs.size ? {
    streamers: displayBd.length,
    hoursStreamed: Math.round(displayBd.reduce((a, r) => a + r.hrsStreamed, 0)),
    watchHours: displayBd.reduce((a, r) => a + r.watchHours, 0),
    peak: displayBd.reduce((a, r) => Math.max(a, r.peak), 0),
  } : bdTotals;
  const bdAllRegions = [...new Set(bdData.map((r) => r.region))].sort();
  const csvCell = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const exportBreakdownCsv = async () => {
    if (!selectedGame || bdExporting) return;
    setBdExporting(true);
    const fmap = {};
    const isKick = bdPlatform === 'kick';
    try {
      const r = await (isKick ? gamesAPI.getKickCoverage : gamesAPI.getCoverage)(selectedGame.id, { followers: 1 });
      (r.data.streamers || []).forEach((s) => { fmap[s.id] = s.followers; });
    } catch (e) { /* export without followers if the lookup fails */ }
    setBdExporting(false);
    const headers = ['Streamer', isKick ? 'Kick URL' : 'Twitch URL', isKick ? 'Subscribers' : 'Followers', 'Peak CCV', 'Avg CCV', 'Hours Streamed', 'Watch Hours', 'Streams', 'Region', 'On Platform'];
    const rows = displayBd.map((r) => [r.name, r.login ? (isKick ? `https://kick.com/${r.login}` : `https://twitch.tv/${r.login}`) : '', fmap[r.id] ?? '', r.peak, r.avg, r.hrsStreamed, r.watchHours, r.streamCount, r.region, r.onPlatform ? 'yes' : '']);
    const csv = [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(selectedGame?.title || 'game').replace(/[^a-z0-9]+/gi, '-')}-${bdPlatform}-coverage.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [linkResult, setLinkResult] = useState(null);

  const handleLinkSingleGame = async (game) => {
    setLinkingSingleGame(true);
    setLinkResult(null);
    try {
      const response = await gamesAPI.linkContent(game.id);
      const linked = response.data.linked || 0;
      setLinkResult({ linked, examples: response.data.examples || [] });
      // Refresh the game content
      const updated = await gamesAPI.getOne(game.id);
      setGameContent(updated.data.game.content || []);
      fetchGames();
      // Clear result after 5 seconds
      setTimeout(() => setLinkResult(null), 5000);
    } catch (err) {
      console.error('Failed to link content:', err);
      setLinkResult({ error: 'Failed to link content. Please try again.' });
      setTimeout(() => setLinkResult(null), 5000);
    } finally {
      setLinkingSingleGame(false);
    }
  };

  const formatViews = (views) => {
    if (!views) return '0';
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  const getFullThumbnailUrl = (url) => {
    if (!url) return null;
    // If it's a relative URL starting with /api, prepend the backend URL
    if (url.startsWith('/api/')) {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      return `${backendUrl}${url}`;
    }
    return url;
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 min-h-screen">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">🎮 Games</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleBulkLinkContent}
            disabled={linkingContent}
            className="btn-pink shadow-pink-glow text-sm px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Scan all content and link to games by title"
          >
            {linkingContent ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Linking...
              </>
            ) : (
              <>
                <Link className="w-4 h-4" />
                Link Content
              </>
            )}
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn-pink shadow-pink-glow text-sm px-3 py-1.5"
          >
            + Add Game
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-3 py-2 text-sm bg-white/5 border border-white rounded-lg text-white placeholder-white focus:outline-none focus:border-sc-pink/40 focus:bg-white/8 transition-all"
        />
      </div>

      {/* Add Game Form */}
      {showAddForm && (
        <div className="bg-gray-900/60 border border-white rounded-xl p-4 mb-4">
          <h3 className="text-base font-bold mb-3 text-white">Add New Game</h3>

          {/* Search Section */}
          <div className="mb-4 p-3 bg-white/5 rounded-lg border border-white">
            <p className="text-xs text-white mb-2">Enter a game title and search to auto-fill details from IGDB</p>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Enter game title to search..."
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  setIgdbFound(false);
                }}
                className="flex-1 px-3 py-1.5 text-sm bg-white/5 border border-white rounded-lg text-white placeholder-white"
              />
              <button
                type="button"
                onClick={handleSearchIGDB}
                disabled={searchingIGDB || formData.title.trim().length < 2}
                className="bg-sc-pink text-white px-4 py-1.5 text-sm rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {searchingIGDB ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    Search IGDB
                  </>
                )}
              </button>
            </div>
            {igdbFound && (
              <div className="mt-2 flex items-center gap-1.5 text-green-600 text-xs">
                <Check className="w-3.5 h-3.5" />
                Game found! Details filled in below.
              </div>
            )}
          </div>

          {/* Preview if cover found */}
          {formData.coverImageUrl && (
            <div className="mb-4 flex items-start gap-3">
              <img
                src={formData.coverImageUrl}
                alt="Cover preview"
                className="w-16 h-20 object-cover rounded shadow"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="text-sm">
                <p className="font-medium text-white">{formData.title}</p>
                {formData.publisher && <p className="text-white text-xs">{formData.publisher}</p>}
                {formData.releaseDate && <p className="text-white text-xs">{formData.releaseDate}</p>}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Game Title *"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="px-3 py-1.5 text-sm bg-white/5 border border-white rounded-lg text-white placeholder-white"
            />
            <input
              type="text"
              placeholder="Publisher"
              value={formData.publisher}
              onChange={(e) => setFormData({ ...formData, publisher: e.target.value })}
              className="px-3 py-1.5 text-sm bg-white/5 border border-white rounded-lg text-white placeholder-white"
            />
            <input
              type="text"
              placeholder="Genres (comma-separated)"
              value={formData.genres}
              onChange={(e) => setFormData({ ...formData, genres: e.target.value })}
              className="px-3 py-1.5 text-sm bg-white/5 border border-white rounded-lg text-white placeholder-white"
            />
            <input
              type="text"
              placeholder="Release Date (YYYY-MM-DD)"
              value={formData.releaseDate}
              onChange={(e) => setFormData({ ...formData, releaseDate: e.target.value })}
              className="px-3 py-1.5 text-sm bg-white/5 border border-white rounded-lg text-white placeholder-white"
            />
            <input
              type="text"
              placeholder="Cover Image URL"
              value={formData.coverImageUrl}
              onChange={(e) => setFormData({ ...formData, coverImageUrl: e.target.value })}
              className="px-3 py-1.5 text-sm bg-white/5 border border-white rounded-lg text-white placeholder-white md:col-span-2"
            />
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="bg-green-600 text-white px-3 py-1.5 text-sm rounded-md hover:bg-green-700">
                Save Game
              </button>
              <button type="button" onClick={() => setShowAddForm(false)} className="bg-white/5 border border-white text-white px-3 py-1.5 text-sm rounded-lg hover:bg-white/10">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Games List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="spinner-pink mx-auto"></div>
          <p className="mt-2 text-white text-xs">Loading games...</p>
        </div>
      ) : games.length === 0 ? (
        <div className="bg-gray-900/60 border border-white rounded-xl p-8 text-center">
          <div className="text-4xl mb-2">🎮</div>
          <h3 className="text-lg font-bold text-white mb-1">No games yet</h3>
          <p className="text-sm text-white">Add your first game to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {games.map((game) => (
            <div key={game.id} className="bg-gray-900/80 border border-white rounded-xl overflow-hidden hover:border-sc-pink/40 transition-all duration-200 cursor-pointer" onClick={() => handleGameClick(game)}>
              {/* Game Cover Image */}
              {game.coverImageUrl ? (
                <img
                  src={game.coverImageUrl}
                  alt={game.title}
                  className="w-full h-32 object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-full h-32 bg-gradient-to-br from-sc-pink to-sc-purple flex items-center justify-center">
                  <Gamepad2 className="w-10 h-10 text-white opacity-50" />
                </div>
              )}

              <div className="p-2">
                {/* Title */}
                <h3 className="font-bold text-sm mb-0.5 truncate text-white" title={game.title}>{game.title}</h3>

                {/* Publisher */}
                {game.publisher && (
                  <p className="text-xs text-white mb-1 truncate">{game.publisher}</p>
                )}

                {/* Release Date */}
                {game.releaseDate && (
                  <div className="flex items-center gap-1 text-xs text-white mb-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(game.releaseDate)}</span>
                  </div>
                )}

                {/* Genres */}
                {game.genres?.length > 0 && (
                  <div className="mb-1">
                    <div className="flex flex-wrap gap-0.5">
                      {game.genres.slice(0, 2).map((genre, idx) => (
                        <span key={idx} className="bg-sc-pink/10 text-sc-pink text-xs px-1.5 py-0.5 rounded">
                          {genre}
                        </span>
                      ))}
                      {game.genres.length > 2 && (
                        <span className="text-xs text-white px-1">+{game.genres.length - 2}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Content Count & Status */}
                <div className="flex justify-between items-center pt-1.5 border-t border-white/10 mt-1">
                  <p className="text-xs text-white">{game._count?.Content || 0} items</p>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteGame(game.id, game.title); }}
                      className="text-white hover:text-red-400 transition-colors"
                      title="Delete game"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    {game.verified && game.coverImageUrl ? (
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" title="Enriched"></span>
                    ) : game.verified ? (
                      <span className="w-1.5 h-1.5 bg-sc-pink rounded-full" title="Verified"></span>
                    ) : (
                      <span className="w-1.5 h-1.5 bg-gray-300 rounded-full" title="Not enriched"></span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Game Content Modal */}
      {selectedGame && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-gray-900 border border-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center gap-4 p-4 border-b border-white/10 bg-gradient-to-r from-sc-pink/10 to-sc-cyan/5">
              {selectedGame.coverImageUrl && (
                <img src={selectedGame.coverImageUrl} alt={selectedGame.title} className="w-16 h-20 object-cover rounded-lg ring-1 ring-white/10" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2.5">
                  <h2 className="text-xl font-bold text-white">{selectedGame.title}</h2>
                  {/* Metacritic sits with the title rather than in the Steam tab:
                      it is an attribute of the game, not of the Steam timeframe,
                      and it stays visible on every tab. Falls back to the Steam
                      payload for games the list query has not carried it on. */}
                  {(selectedGame.metacriticScore || steam?.metacritic?.score) && (
                    <a
                      href={selectedGame.metacriticUrl || steam?.metacritic?.url || undefined}
                      target="_blank" rel="noopener"
                      title="Metacritic score"
                      className="inline-flex items-center gap-1.5 text-xs text-white hover:text-sc-pink"
                    >
                      <span className="font-bold px-1.5 py-0.5 rounded bg-sc-pink text-white tabular-nums">
                        {selectedGame.metacriticScore || steam?.metacritic?.score}
                      </span>
                      Metacritic
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {selectedGame.publisher && <p className="text-sm text-white">{selectedGame.publisher}</p>}
                <p className="text-xs text-white mt-1">{gameContent.length} content items linked</p>
              </div>
              <button
                onClick={() => handleLinkSingleGame(selectedGame)}
                disabled={linkingSingleGame}
                className="px-3 py-1.5 bg-sc-pink/15 border border-sc-pink/25 text-sc-pink text-sm font-semibold rounded-lg hover:bg-sc-pink/25 disabled:opacity-50 flex items-center gap-1.5 transition-all"
              >
                {linkingSingleGame ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link className="w-3.5 h-3.5" />}
                {linkingSingleGame ? 'Linking...' : 'Link Content'}
              </button>
              <button onClick={closeModal} className="text-white hover:text-white p-2">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Link progress/result */}
            {(linkingSingleGame || linkResult) && (
              <div className={`mx-4 mt-3 px-4 py-2.5 rounded-lg text-sm ${
                linkResult?.error ? 'bg-red-500/10 border border-red-500/20 text-red-400' :
                linkResult ? 'bg-green-500/10 border border-green-500/20 text-green-400' :
                'bg-sc-pink/10 border border-sc-pink/20 text-sc-pink'
              }`}>
                {linkingSingleGame ? (
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Scanning content for "{selectedGame?.title}"...</span>
                    </div>
                    <div className="w-full bg-black/10 rounded-full h-1.5">
                      <div className="bg-sc-pink h-1.5 rounded-full animate-pulse" style={{ width: '60%' }} />
                    </div>
                  </div>
                ) : linkResult?.error ? (
                  <span>{linkResult.error}</span>
                ) : (
                  <div>
                    <span className="font-medium">
                      {linkResult.linked > 0 ? `Linked ${linkResult.linked} content items` : 'No new content found to link'}
                    </span>
                    {linkResult.examples?.length > 0 && (
                      <p className="text-xs mt-1 opacity-70">e.g. {linkResult.examples.slice(0, 2).join(', ')}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-5 px-4 border-b border-white/10">
              <button
                onClick={() => setModalTab('content')}
                className={`px-1 py-2.5 text-sm font-medium border-b-2 -mb-px ${modalTab === 'content' ? 'border-sc-pink text-sc-pink' : 'border-transparent text-white hover:text-white'}`}
              >Linked Content</button>
              <button
                onClick={() => setModalTab('breakdown')}
                className={`px-1 py-2.5 text-sm font-medium border-b-2 -mb-px ${modalTab === 'breakdown' ? 'border-sc-pink text-sc-pink' : 'border-transparent text-white hover:text-white'}`}
              >Stream Breakdown</button>
              <button
                onClick={() => setModalTab('videos')}
                className={`px-1 py-2.5 text-sm font-medium border-b-2 -mb-px ${modalTab === 'videos' ? 'border-sc-pink text-sc-pink' : 'border-transparent text-white hover:text-white'}`}
              >Video Breakdown</button>
              <button
                onClick={() => setModalTab('steam')}
                className={`px-1 py-2.5 text-sm font-medium border-b-2 -mb-px ${modalTab === 'steam' ? 'border-sc-pink text-sc-pink' : 'border-transparent text-white hover:text-white'}`}
              >Steam</button>
              <button
                onClick={() => setModalTab('console')}
                className={`px-1 py-2.5 text-sm font-medium border-b-2 -mb-px ${modalTab === 'console' ? 'border-sc-pink text-sc-pink' : 'border-transparent text-white hover:text-white'}`}
              >Console Data</button>
            </div>

            {/* Modal Content */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-170px)]">
              {modalTab === 'breakdown' ? (
              <div>
                {/* Source platform — Twitch (10-min scanner + sweeps) or Kick (hourly directory sweep) */}
                <div className="flex items-center gap-1.5 mb-3">
                  <span className="text-xs text-white mr-1">Platform:</span>
                  {[['twitch', 'Twitch'], ['kick', 'Kick']].map(([v, l]) => (
                    <button
                      key={v}
                      onClick={() => setBdPlatform(v)}
                      className={`text-xs px-2 py-0.5 rounded-full border ${bdPlatform === v ? 'bg-sc-pink/20 border-sc-pink/40 text-sc-pink' : 'border-white text-white hover:text-white hover:border-white'}`}
                    >{l}</button>
                  ))}
                </div>
                {coverageLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-sc-pink" />
                    <p className="text-sm text-white mt-2">Loading stream coverage…</p>
                  </div>
                ) : bdData.length === 0 ? (
                  <div className="text-center py-8">
                    <Gamepad2 className="w-12 h-12 mx-auto text-white/20 mb-2" />
                    <p className="text-white">No {bdPlatform === 'kick' ? 'Kick' : 'Twitch'} stream-coverage data for this game yet.</p>
                    <p className="text-sm text-white mt-1">{coverage && coverage.tracked === false
                      ? (bdPlatform === 'kick' ? 'No Kick category is mapped to this game yet.' : 'This game isn’t in the tracked coverage set.')
                      : (bdPlatform === 'kick' ? 'Coverage builds as the hourly Kick sweep observes streams.' : 'Coverage builds as streams are observed.')}</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                      <span className="text-xs text-white mr-1">Timeframe:</span>
                      {[['1', 'Day'], ['30', '30d'], ['60', '60d'], ['90', '90d'], ['180', '180d'], ['365', '365d'], ['all', 'All'], ['custom', 'Custom']].map(([v, l]) => (
                        <button
                          key={v}
                          onClick={() => setBdRange(v)}
                          className={`text-xs px-2 py-0.5 rounded-full border ${bdRange === v ? 'bg-sc-pink/20 border-sc-pink/40 text-sc-pink' : 'border-white text-white hover:text-white hover:border-white'}`}
                        >{l}</button>
                      ))}
                      {bdRange === 'custom' && (
                        <span className="flex items-center gap-1.5 ml-1">
                          <input type="date" value={bdCustom.from} onChange={(e) => setBdCustom((c) => ({ ...c, from: e.target.value }))} className="text-xs bg-white/5 border border-white rounded px-1.5 py-0.5 text-white" />
                          <span className="text-white text-xs">→</span>
                          <input type="date" value={bdCustom.to} onChange={(e) => setBdCustom((c) => ({ ...c, to: e.target.value }))} className="text-xs bg-white/5 border border-white rounded px-1.5 py-0.5 text-white" />
                        </span>
                      )}
                      <div className="relative ml-2">
                        <button
                          onClick={() => setBdLangOpen((o) => !o)}
                          className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 ${bdLangs.size ? 'bg-sc-pink/20 border-sc-pink/40 text-sc-pink' : 'border-white text-white hover:text-white hover:border-white'}`}
                        >{bdLangs.size ? `${bdLangs.size} language${bdLangs.size > 1 ? 's' : ''}` : 'Language'} ▾</button>
                        {bdLangOpen && (
                          <div className="absolute z-10 mt-1 left-0 w-44 max-h-60 overflow-y-auto bg-gray-900 border border-white rounded-lg p-1 shadow-xl">
                            {bdLangs.size > 0 && (
                              <button onClick={() => setBdLangs(new Set())} className="w-full text-left text-xs px-2 py-1 text-white hover:text-white hover:bg-white/5 rounded">Clear all</button>
                            )}
                            {bdAllRegions.map((lang) => (
                              <button key={lang} onClick={() => toggleLang(lang)} className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white/5 flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-sm border flex-shrink-0 ${bdLangs.has(lang) ? 'bg-sc-pink border-sc-pink' : 'border-white'}`} />
                                <span className="text-white">{lang}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3 mb-3">
                      {[['Streamers', displayTotals.streamers.toLocaleString()], ['Hours streamed', displayTotals.hoursStreamed.toLocaleString()], ['Watch hours', displayTotals.watchHours.toLocaleString()], ['Peak viewers', displayTotals.peak.toLocaleString()]].map(([label, val]) => (
                        <div key={label} className="bg-white/5 border border-white rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-white">{val}</div>
                          <div className="text-xs text-white">{label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      <span className="text-xs text-white mr-1">Region split (by watch-hours):</span>
                      {bdRegions.map((r) => (
                        <span key={r.region} className="px-2 py-0.5 bg-sc-pink/15 text-sc-pink rounded-full text-xs font-medium">{r.region} {r.pct}%</span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-white">{bdPlatform === 'kick' ? 'Kick' : 'Twitch'} · click a column to sort · click a row for individual streams.</p>
                      <button
                        onClick={exportBreakdownCsv}
                        disabled={bdExporting}
                        className="text-xs px-2.5 py-1 border border-white rounded-lg hover:bg-white/5 text-white flex items-center gap-1.5 disabled:opacity-60"
                      >
                        {bdExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        {bdExporting ? 'Exporting…' : 'Export CSV'}
                      </button>
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-white border-b border-white/10">
                          <th className="text-left py-2 font-medium">Streamer</th>
                          {[['peak', 'Peak CCV'], ['avg', 'Avg CCV'], ['hrsStreamed', 'Hrs Streamed'], ['watchHours', 'Watch Hours'], ['streamCount', 'Streams'], ['region', 'Region']].map(([k, lbl]) => (
                            <th
                              key={k}
                              onClick={() => toggleBdSort(k)}
                              className={`py-2 font-medium cursor-pointer select-none hover:text-sc-pink ${k === 'region' ? 'text-left pl-3' : 'text-right'} ${bdSort.key === k ? 'text-sc-pink' : ''}`}
                            >{lbl}{bdArrow(k)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {displayBd.map((r) => (
                          <React.Fragment key={r.name}>
                            <tr className={`border-b border-white/5 hover:bg-white/5 ${(r.sessions || []).length ? 'cursor-pointer' : ''}`} onClick={() => (r.sessions || []).length && toggleExpand(r.name)}>
                              <td className="py-2 font-medium text-white">
                                <span className="inline-block w-4 text-white select-none">{(r.sessions || []).length ? (bdExpanded.has(r.name) ? '▾' : '▸') : ''}</span>
                                <a href={r.login ? (bdPlatform === 'kick' ? `https://kick.com/${r.login}` : `https://twitch.tv/${r.login}`) : undefined} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="hover:text-sc-pink hover:underline">{r.name}</a>
                                {r.onPlatform && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-sc-pink/15 text-sc-pink rounded align-middle">on platform</span>}
                              </td>
                              <td className="py-2 text-right text-white">{r.peak.toLocaleString()}</td>
                              <td className="py-2 text-right text-white">{r.avg.toLocaleString()}</td>
                              <td className="py-2 text-right text-white">{r.hrsStreamed}</td>
                              <td className="py-2 text-right font-semibold text-white">{r.watchHours.toLocaleString()}</td>
                              <td className="py-2 text-right text-white">{r.streamCount == null ? '—' : r.streamCount}</td>
                              <td className="py-2 text-left pl-3"><span className="px-2 py-0.5 bg-white/10 rounded text-xs text-white">{r.region}</span></td>
                            </tr>
                            {bdExpanded.has(r.name) && (r.sessions || []).map((s, i) => (
                              <tr key={r.name + '-' + i} className="bg-white/5 text-xs text-white border-b border-white/5">
                                <td className="py-1.5 pl-8">{fmtDay(s.date)}</td>
                                <td className="py-1.5 text-right">{s.peak.toLocaleString()}</td>
                                <td className="py-1.5 text-right">{s.avg.toLocaleString()}</td>
                                <td className="py-1.5 text-right">{s.hrsStreamed}</td>
                                <td className="py-1.5 text-right">{s.watchHours.toLocaleString()}</td>
                                <td className="py-1.5 text-right">—</td>
                                <td></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              ) : modalTab === 'console' ? (
                consoleLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-sc-pink" />
                    <p className="text-sm text-white mt-2">Loading console data…</p>
                  </div>
                ) : !consoleData?.nintendoNsuid && !consoleData?.xboxProductId ? (
                  <div className="text-center py-8">
                    <Gamepad2 className="w-12 h-12 mx-auto text-white/20 mb-2" />
                    <p className="text-white">Not linked to a console store yet.</p>
                    <p className="text-sm text-white mt-1 max-w-2xl mx-auto">
                      Switch resolves automatically from the game title. Xbox needs its store URL pasted in,
                      because Microsoft blocks automated search. Neither console publishes player counts —
                      Steam is the only platform that does — so this tab is store data only.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="text-xs text-white mb-1">Nintendo Switch</div>
                        <div className="text-sm text-white">
                          {consoleData.nintendoNsuid
                            ? <>Linked <span className="text-white/70">· {consoleData.nintendoNsuid.split(',').join(', ')}</span></>
                            : 'Not on the eShop, or no exact title match'}
                        </div>
                      </div>
                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="text-xs text-white mb-1">Xbox</div>
                        <div className="text-sm text-white">
                          {consoleData.xboxProductId
                            ? <>Linked <span className="text-white/70">· {consoleData.xboxProductId}</span></>
                            : 'Needs its store URL linking by hand'}
                        </div>
                      </div>
                    </div>

                    {consoleData.ratings?.length > 0 && (
                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="text-sm font-semibold text-white mb-2">Xbox Rating</div>
                        <div className="flex flex-wrap gap-3">
                          {consoleData.ratings.map((rt) => (
                            <div key={rt.window} className="bg-[#17121f] border border-white/30 rounded-lg px-3 py-1.5">
                              <div className="text-xs text-white">
                                {rt.window === '7Days' ? 'Last 7 Days' : rt.window === '30Days' ? 'Last 30 Days' : 'All Time'}
                              </div>
                              <div className="text-lg font-bold text-white tabular-nums">{rt.rating?.toFixed(1)}</div>
                              <div className="text-xs text-white">{rt.ratingCount?.toLocaleString()} ratings</div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-white mt-2">
                          A star score, not written reviews — Xbox publishes no review text. Movement between the
                          windows is the signal: a 30-day figure above the all-time one means sentiment is improving.
                        </p>
                      </div>
                    )}

                    <div className="bg-[#241d33] border border-white rounded-xl p-3">
                      <div className="text-sm font-semibold text-white mb-2">
                        Sales <span className="text-white font-normal">({consoleData.sales?.length || 0})</span>
                      </div>
                      {!consoleData.sales?.length ? (
                        <p className="text-sm text-white">No sale recorded since tracking began.</p>
                      ) : (
                        <div className="divide-y divide-white/10">
                          {consoleData.sales.map((sale) => (
                            <div key={`${sale.platform}-${sale.storeId}-${sale.firstSeenAt}`} className="flex items-center gap-2 py-2">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex-none ${sale.isCurrent ? 'bg-sc-pink text-white' : 'bg-white/15 text-white'}`}>
                                {sale.isCurrent ? 'LIVE' : 'ENDED'}
                              </span>
                              <span className="text-sm text-white w-16 flex-none">
                                {sale.platform === 'switch' ? 'Switch' : 'Xbox'}
                              </span>
                              {sale.platform === 'switch' && consoleData.nintendoNsuid?.includes(',') && (
                                <span className="text-xs text-white/70 flex-none tabular-nums">{sale.storeId}</span>
                              )}
                              <span className="text-sm font-semibold text-sc-pink flex-none w-14">−{sale.discountPercent}%</span>
                              <span className="text-sm text-white flex-1 tabular-nums">
                                {sale.currency} {sale.regularPrice} → {sale.salePrice}
                              </span>
                              <span className="text-xs text-white tabular-nums flex-none">
                                {new Date(sale.firstSeenAt).toLocaleDateString()}
                                {!sale.isCurrent && ` – ${new Date(sale.lastSeenAt).toLocaleDateString()}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-white mt-2">
                        A console sale drives its own traffic — treat a rise during one the way you would a Steam sale.
                        Neither store publishes sale history, so these are only recorded from the day tracking started.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-2 text-xs text-white pt-1">
                      <span>Sources: Nintendo eShop and Microsoft Store. Data provided as is, without warranty of accuracy.</span>
                      <span>Not affiliated with or endorsed by Nintendo or Microsoft.</span>
                    </div>
                  </div>
                )
              ) : modalTab === 'steam' ? (
                steamLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-sc-pink" />
                    <p className="text-sm text-white mt-2">Loading Steam data…</p>
                  </div>
                ) : steamError ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 mx-auto text-sc-pink/60 mb-2" />
                    <p className="text-white">Couldn’t load Steam data.</p>
                    <p className="text-sm text-white mt-1 max-w-xl mx-auto">{steamError}</p>
                  </div>
                ) : !steam?.linked ? (
                  <div className="text-center py-8">
                    <Gamepad2 className="w-12 h-12 mx-auto text-white/20 mb-2" />
                    <p className="text-white">This game isn’t linked to Steam yet.</p>
                    <p className="text-sm text-white mt-1">
                      A Steam App ID has to be attached before anything is recorded. Linking is done deliberately,
                      one game at a time, because Steam lists demos, soundtracks and editions under near-identical names.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Headline tiles */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="flex items-center gap-1.5 text-xs text-white mb-1">
                          <Users className="w-3.5 h-3.5 text-sc-pink" /> Playing Now
                        </div>
                        <div className="text-2xl font-bold text-white tabular-nums">
                          {steam.current ? steam.current.players.toLocaleString() : '—'}
                        </div>
                        <div className="text-xs text-white">
                          {steam.current
                            ? `as of ${new Date(steam.current.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                            : 'no reading yet'}
                        </div>
                      </div>

                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="flex items-center gap-1.5 text-xs text-white mb-1">
                          <Eye className="w-3.5 h-3.5 text-sc-pink" /> Peak · {steamWindowLabel}
                        </div>
                        <div className="text-2xl font-bold text-white tabular-nums">
                          {steamWindowPeak ? steamWindowPeak.peakCcu.toLocaleString() : '—'}
                        </div>
                        <div className="text-xs text-white">
                          {steamWindowPeak
                            ? `on ${new Date(`${steamWindowPeak.date}T00:00:00Z`).toLocaleDateString()}`
                            : 'recording starts at launch'}
                        </div>
                      </div>

                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="flex items-center gap-1.5 text-xs text-white mb-1">
                          <MessageSquare className="w-3.5 h-3.5 text-sc-pink" /> Review Score
                        </div>
                        <div className="text-2xl font-bold text-white tabular-nums">
                          {steam.reviews?.positivePct != null ? `${steam.reviews.positivePct}%` : '—'}
                        </div>
                        <div className="text-xs text-white">
                          {steam.reviews?.total
                            ? `${steam.reviews.scoreDesc} · ${steam.reviews.total.toLocaleString()} reviews`
                            : 'no reviews yet'}
                        </div>
                        {/* Movement inside the selected window, not just the all-time
                            score — the headline % is the game's lifetime rating, this is
                            what actually happened in the period being looked at. */}
                        {steamWindowReviews.added > 0 && (
                          <div className="text-xs text-white mt-1">
                            <span className="text-sc-pink">+{steamWindowReviews.added.toLocaleString()}</span>
                            {' '}in {steamWindowLabel}
                            {steamWindowReviews.added > 0 && (
                              <> · {Math.round(steamWindowReviews.positive / steamWindowReviews.added * 100)}% positive</>
                            )}
                          </div>
                        )}
                        {/* Bought-on-Steam vs key recipients. The gap is the audience
                            our key drops create, and it is the only public signal of it.
                            Follows the selected timeframe where we have the history for
                            it, and says "all time" plainly where we do not. */}
                        {steamWindowSplit ? (
                          <div className="text-xs text-white mt-1">
                            {steamWindowSplit.bought.toLocaleString()} bought on Steam
                            <span className="text-white/40 mx-1">·</span>
                            <span className="text-sc-pink">{steamWindowSplit.keys.toLocaleString()} from keys</span>
                            {' '}({steamWindowSplit.pct}%) {steamWindowSplit.label}
                          </div>
                        ) : steam.reviews?.fromKeys != null && steam.reviews.total > 0 ? (
                          <div className="text-xs text-white mt-1">
                            {steam.reviews.boughtOnSteam.toLocaleString()} bought on Steam
                            <span className="text-white/40 mx-1">·</span>
                            <span className="text-sc-pink">{steam.reviews.fromKeys.toLocaleString()} from keys</span>
                            {' '}({Math.round(steam.reviews.fromKeys / steam.reviews.total * 100)}%) all time
                          </div>
                        ) : null}
                      </div>

                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="flex items-center gap-1.5 text-xs text-white mb-1">
                          <Megaphone className="w-3.5 h-3.5 text-sc-pink" /> Latest Update
                        </div>
                        <div className="text-sm font-semibold text-white leading-snug line-clamp-2">
                          {steam.news?.[0]?.title || '—'}
                        </div>
                        <div className="text-xs text-white">
                          {steam.news?.[0] ? new Date(steam.news[0].publishedAt).toLocaleDateString() : 'none in window'}
                        </div>
                      </div>
                    </div>

                    {/* Review bombing — only shown when Steam is actually hiding reviews */}
                    {steam.reviews?.hiddenBySteam > 0 && (
                      <div className="bg-[#241d33] border border-sc-pink rounded-xl p-3 flex gap-2.5">
                        <AlertTriangle className="w-4 h-4 text-sc-pink flex-none mt-0.5" />
                        <div className="text-sm text-white">
                          <span className="font-semibold">
                            Steam is hiding {steam.reviews.hiddenBySteam.toLocaleString()} reviews from the public score.
                          </span>{' '}
                          It classes these as off-topic — usually a review-bombing episode. The displayed score
                          understates the reaction, which is worth knowing before quoting it to a client.
                        </div>
                      </div>
                    )}

                    {/* Timeframe */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-white mr-1">Timeframe:</span>
                      {[['7', '7d'], ['30', '30d'], ['90', '90d'], ['365', '365d']].map(([v, l]) => (
                        <button key={v} onClick={() => setSteamRange(v)}
                          className={`text-xs px-2 py-0.5 rounded-full border ${steamRange === v ? 'bg-sc-pink/20 border-sc-pink/40 text-sc-pink' : 'border-white text-white hover:border-white'}`}
                        >{l}</button>
                      ))}
                    </div>

                    {/* Total creator coverage — everyone we recorded, campaign or not */}
                    {steam.coverage && (steam.coverage.streams > 0 || steam.coverage.videos > 0) && (
                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="text-sm font-semibold text-white mb-2">
                          All Creator Coverage Recorded <span className="text-white font-normal">· last {steam.window} days</span>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                          {/* Dots match the chart series colours so the mapping is obvious */}
                          <div>
                            <div className="flex items-center gap-1.5 text-xs text-white">
                              <span className="w-2 h-2 rounded-full flex-none" style={{ background: '#A970FF' }} />Twitch Creators
                            </div>
                            <div className="text-xl font-bold text-white tabular-nums">{steam.coverage.twitchCreators.toLocaleString()}</div>
                            <div className="text-xs text-white">{steam.coverage.streams.toLocaleString()} Streams</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 text-xs text-white">
                              <span className="w-2 h-2 rounded-full flex-none" style={{ background: '#A970FF' }} />Twitch Watch Hours
                            </div>
                            <div className="text-xl font-bold text-white tabular-nums">{steam.coverage.watchHours.toLocaleString()}</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 text-xs text-white">
                              <span className="w-2 h-2 rounded-full flex-none" style={{ background: '#FF0000' }} />YouTube Channels
                            </div>
                            <div className="text-xl font-bold text-white tabular-nums">{steam.coverage.youtubeChannels.toLocaleString()}</div>
                            <div className="text-xs text-white">{steam.coverage.videos.toLocaleString()} Videos</div>
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 text-xs text-white">
                              <span className="w-2 h-2 rounded-full flex-none" style={{ background: '#FF0000' }} />YouTube Views
                            </div>
                            <div className="text-xl font-bold text-white tabular-nums">{steam.coverage.views.toLocaleString()}</div>
                          </div>
                        </div>
                        {/* Capture limits, stated briefly. These totals are a floor, not
                            a census — see also the top-100 video cap in
                            youtubeVideoCoverageService (MAX_VIDEOS / MAX_PAGES_PER_TERM),
                            which binds well above 100 views on high-volume games. */}
                        <p className="text-xs text-white mt-2">
                          Streams under {steam.coverage.viewerFloor ?? 10} viewers aren’t captured
                          <span className="text-white/40 mx-2">|</span>
                          Videos under 100 views aren’t captured
                        </p>
                      </div>
                    )}

                    {/* Players vs creator coverage */}
                    <div className="bg-[#241d33] border border-white rounded-xl p-3">
                      <div className="text-sm font-semibold text-white mb-2">
                        {steamHasCoverage ? 'Players Vs Creator Coverage' : 'Players Over Time'}
                      </div>
                      {steamChart.length < 2 ? (
                        <div className="text-center py-10">
                          <p className="text-white">Not enough recorded history yet.</p>
                          <p className="text-sm text-white mt-1">
                            {steam.recordingSince
                              ? `Recording since ${new Date(steam.recordingSince).toLocaleDateString()}. Steam publishes no history, so this chart can only grow forward from that date.`
                              : 'Nothing recorded yet. Steam publishes no history — the chart starts from the first reading.'}
                          </p>
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={280}>
                          <ComposedChart data={steamChart} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="steamCcu" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#fe2388" stopOpacity={0.4} />
                                <stop offset="100%" stopColor="#fe2388" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
                            <XAxis dataKey="label" tick={{ fill: '#fff', fontSize: 11 }} stroke="rgba(255,255,255,0.25)" />
                            {/* Left axis = players (linear). The two coverage series sit
                                on their own logarithmic axes: creator activity spans
                                orders of magnitude and on a linear scale the quiet days
                                collapse to nothing. Trade-off, accepted deliberately:
                                on a log axis bar HEIGHTS show which days were busy, not
                                how many times bigger one day was. Hover for real figures. */}
                            <YAxis
                              yAxisId="left" tick={{ fill: '#fff', fontSize: 11 }}
                              stroke="rgba(255,255,255,0.25)" width={76}
                              label={{ value: 'Player Count', angle: -90, position: 'insideLeft', fill: '#fff', fontSize: 11, offset: 6, style: { textAnchor: 'middle' } }}
                            />
                            <YAxis
                              yAxisId="views" orientation="right" scale="log" domain={[1, 'auto']}
                              allowDataOverflow tick={{ fill: '#fff', fontSize: 11 }}
                              stroke="rgba(255,255,255,0.25)" width={58}
                            />
                            <YAxis yAxisId="hours" orientation="right" scale="log" domain={[1, 'auto']} allowDataOverflow hide />
                            <Tooltip
                              contentStyle={{ background: '#241d33', border: '1px solid #fff', borderRadius: 10, color: '#fff' }}
                              labelStyle={{ color: '#fff' }}
                              formatter={(v, n) => [v == null ? '—' : Number(v).toLocaleString(), n]}
                            />
                            {steamNewsMarks.map((n) => (
                              <ReferenceLine
                                key={n.gid} yAxisId="left" x={n.label}
                                stroke="#ffffff" strokeWidth={2} strokeDasharray="4 3"
                              />
                            ))}
                            <Bar yAxisId="hours" dataKey="watchHours" name="Twitch watch hours" fill="#A970FF" radius={[2, 2, 0, 0]} minPointSize={3} />
                            <Bar yAxisId="views" dataKey="views" name="YouTube views" fill="#FF0000" radius={[2, 2, 0, 0]} minPointSize={3} />
                            {/* Dark casing so the line stays legible over a bar */}
                            <Area
                              yAxisId="left" type="monotone" dataKey="peak" legendType="none" tooltipType="none"
                              stroke="#241d33" strokeWidth={7} fill="none" connectNulls={false} dot={false} activeDot={false}
                              isAnimationActive={false}
                            />
                            <Area
                              yAxisId="left" type="monotone" dataKey="peak" name="Peak players"
                              stroke="#fe2388" strokeWidth={3} fill="url(#steamCcu)" connectNulls={false}
                              dot={{ r: 3.5, fill: '#fe2388', stroke: '#241d33', strokeWidth: 1.5 }}
                              activeDot={{ r: 6, fill: '#fe2388', stroke: '#fff', strokeWidth: 2 }}
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      )}
                      {steamChart.length >= 2 && (
                        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mt-1">
                          <span className={`flex items-center gap-1.5 text-xs ${steamPlayerDays >= 2 ? 'text-white' : 'text-white'}`}>
                            <svg width="18" height="8" aria-hidden="true">
                              <line x1="0" y1="4" x2="18" y2="4" stroke="#fe2388" strokeWidth="2" strokeOpacity={steamPlayerDays >= 2 ? 1 : 0.4} />
                              <circle cx="9" cy="4" r="2.5" fill="#fe2388" fillOpacity={steamPlayerDays >= 2 ? 1 : 0.4} />
                            </svg>
                            Peak Players
                            {steamPlayerDays < 2 && (
                              <span className="text-white">
                                ({steamPlayerDays === 0 ? 'no readings yet' : '1 reading so far'})
                              </span>
                            )}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-white">
                            <span className="w-3 h-3 rounded-sm flex-none" style={{ background: '#A970FF' }} />
                            Twitch Watch Hours
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-white">
                            <span className="w-3 h-3 rounded-sm flex-none" style={{ background: '#FF0000' }} />
                            YouTube Views
                          </span>
                          {steamNewsMarks.length > 0 && (
                            <span className="flex items-center gap-1.5 text-xs text-white">
                              <svg width="18" height="8" aria-hidden="true">
                                <line x1="0" y1="4" x2="18" y2="4" stroke="#ffffff" strokeWidth="2" strokeDasharray="4 3" />
                              </svg>
                              Official Update
                            </span>
                          )}
                        </div>
                      )}
                      {/* Chart caption removed at request. The one note kept is the
                          "not enough readings yet" state, because without it a chart
                          with no line looks broken rather than new. */}
                      {steamChart.length >= 2 && steamPlayerDays < 2 && (
                        <p className="text-xs text-white mt-1.5">
                          The players line needs two days of recording before it can draw
                          {steamPlayerDays === 1 ? ' — one reading so far, shown as a dot.' : '.'}
                        </p>
                      )}
                    </div>

                    {/* Store featuring — the third confound after patches and sales.
                        Spells persist after a placement ends, because Steam keeps no
                        history of its own front page. */}
                    {steam.featured?.length > 0 && (
                      <div className="bg-[#241d33] border border-white rounded-xl p-3">
                        <div className="text-sm font-semibold text-white mb-2">
                          Featured On Steam <span className="text-white font-normal">({steam.featured.length})</span>
                        </div>
                        <div className="divide-y divide-white/10">
                          {steam.featured.map((f) => (
                            <div key={`${f.section}-${f.firstSeenAt}`} className="flex items-center gap-2 py-2">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex-none ${f.isCurrent ? 'bg-sc-pink text-white' : 'bg-white/15 text-white'}`}>
                                {f.isCurrent ? 'LIVE' : 'ENDED'}
                              </span>
                              <span className="text-sm text-white flex-1">{f.sectionName || f.section}</span>
                              {f.discountPercent > 0 && (
                                <span className="text-xs font-semibold text-sc-pink flex-none">−{f.discountPercent}%</span>
                              )}
                              <span className="text-xs text-white tabular-nums flex-none">
                                {new Date(f.firstSeenAt).toLocaleDateString()}
                                {!f.isCurrent && ` – ${new Date(f.lastSeenAt).toLocaleDateString()}`}
                              </span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-white mt-2">
                          A front-page placement drives large traffic on its own — treat a rise during one as the placement, not the coverage.
                        </p>
                      </div>
                    )}

                    {/* Achievement completion — how far players actually get */}
                    {steam.achievements?.length > 0 && (() => {
                      const a = steam.achievements;
                      const top = a.slice(0, 5);
                      const bottom = a.slice(-5).reverse();
                      const bar = (p) => (
                        <span className="flex-1 h-1.5 rounded-full bg-white/15 overflow-hidden">
                          <span className="block h-full rounded-full bg-sc-pink" style={{ width: `${Math.max(p, 1)}%` }} />
                        </span>
                      );
                      const row = (x) => (
                        <div key={x.apiName} className="flex items-center gap-2 py-1">
                          <span className="text-xs text-white w-11 text-right tabular-nums flex-none">{x.percent.toFixed(1)}%</span>
                          {bar(x.percent)}
                          <span className="text-xs text-white flex-none w-48 truncate" title={x.displayName || x.apiName}>
                            {x.displayName || x.apiName}
                          </span>
                        </div>
                      );
                      return (
                        <div className="bg-[#241d33] border border-white rounded-xl p-3">
                          <div className="text-sm font-semibold text-white mb-1">
                            Achievement Completion <span className="text-white font-normal">({a.length} achievements)</span>
                          </div>
                          <p className="text-xs text-white mb-2">
                            Share of owners who unlocked each one — how far players actually get, rather than how many turned up.
                          </p>
                          <div className="text-xs text-white mb-1">Most earned</div>
                          {top.map(row)}
                          <div className="text-xs text-white mt-2 mb-1">Least earned</div>
                          {bottom.map(row)}
                        </div>
                      );
                    })()}

                    {/* Official updates */}
                    <div className="bg-[#241d33] border border-white rounded-xl p-3">
                      <div className="text-sm font-semibold text-white mb-2">
                        Official Updates <span className="text-white font-normal">({steam.news?.length || 0} in window)</span>
                      </div>
                      {!steam.news?.length ? (
                        <p className="text-sm text-white">No developer announcements in this window.</p>
                      ) : (
                        <div className="divide-y divide-white/10">
                          {steam.news.slice(0, 12).map((n) => (
                            <a key={n.gid} href={n.url} target="_blank" rel="noopener"
                              className="flex items-start gap-2 py-2 group">
                              <span className="text-xs text-white tabular-nums w-20 flex-none pt-0.5">
                                {new Date(n.publishedAt).toLocaleDateString()}
                              </span>
                              <span className="text-sm text-white group-hover:text-sc-pink flex-1">{n.title}</span>
                              <ExternalLink className="w-3.5 h-3.5 text-white/40 flex-none mt-0.5" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Attribution + disclaimer — required by Valve's API terms.
                        The Steam link must stay followable: nofollow is explicitly forbidden. */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-white pt-1">
                      <span>Source:</span>
                      <a href={steam.storeUrl} target="_blank" rel="noopener" className="text-sc-pink hover:underline inline-flex items-center gap-1">
                        Steam <ExternalLink className="w-3 h-3" />
                      </a>
                      <span>· Data provided as is, without warranty of accuracy.</span>
                      <span>· Not affiliated with or endorsed by Valve Corporation.</span>
                    </div>
                  </div>
                )
              ) : loadingContent ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-sc-pink" />
                  <p className="text-sm text-white mt-2">Loading content...</p>
                </div>
              ) : modalTab === 'videos' ? (
                vcLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-sc-pink" />
                    <p className="text-sm text-white mt-2">Loading video coverage…</p>
                  </div>
                ) : vcCreators.length === 0 ? (
                  <div className="text-center py-8">
                    <Gamepad2 className="w-12 h-12 mx-auto text-white/20 mb-2" />
                    <p className="text-white">No YouTube video coverage for this game yet.</p>
                    <p className="text-sm text-white mt-1">Coverage is discovered from YouTube uploads on a rolling 30-day window.</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                      <span className="text-xs text-white mr-1">Timeframe:</span>
                      {[['1', 'Day'], ['7', '7d'], ['30', '30d']].map(([v, l]) => (
                        <button key={v} onClick={() => setVcRange(v)}
                          className={`text-xs px-2 py-0.5 rounded-full border ${vcRange === v ? 'bg-sc-pink/20 border-sc-pink/40 text-sc-pink' : 'border-white text-white hover:text-white hover:border-white'}`}
                        >{l}</button>
                      ))}
                      <div className="relative ml-1">
                        <button onClick={() => setVcLangOpen((o) => !o)}
                          className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1 ${vcLangs.size ? 'bg-sc-pink/20 border-sc-pink/40 text-sc-pink' : 'border-white text-white hover:text-white hover:border-white'}`}
                        >{vcLangs.size ? `${vcLangs.size} language${vcLangs.size > 1 ? 's' : ''}` : 'Language'} ▾</button>
                        {vcLangOpen && (
                          <div className="absolute z-10 mt-1 left-0 w-44 max-h-60 overflow-y-auto bg-gray-900 border border-white rounded-lg p-1 shadow-xl">
                            {vcLangs.size > 0 && (
                              <button onClick={() => setVcLangs(new Set())} className="w-full text-left text-xs px-2 py-1 text-white hover:text-white hover:bg-white/5 rounded">Clear all</button>
                            )}
                            {vcAllRegions.map((lang) => (
                              <button key={lang} onClick={() => toggleVcLang(lang)} className="w-full text-left text-xs px-2 py-1 rounded hover:bg-white/5 flex items-center gap-2">
                                <span className={`w-3 h-3 rounded-sm border flex-shrink-0 ${vcLangs.has(lang) ? 'bg-sc-pink border-sc-pink' : 'border-white'}`} />
                                <span className="text-white">{lang}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-[11px] text-white ml-1">YouTube uploads · 30-day rolling</span>
                    </div>
                    {vc.regions && vc.regions.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        <span className="text-xs text-white mr-1">Region split (by views):</span>
                        {vc.regions.map((r) => (
                          <span key={r.region} className="px-2 py-0.5 bg-sc-pink/15 text-sc-pink rounded-full text-xs font-medium">{r.region} {r.pct}%</span>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-4 gap-3 mb-3">
                      {[['Creators', vc.totals.creators], ['Videos', vc.totals.videos], ['Total views', vc.totals.views], ['Avg engagement', vc.totals.avgEngagement + '%']].map(([label, val]) => (
                        <div key={label} className="bg-white/5 border border-white rounded-lg p-3 text-center">
                          <div className="text-lg font-bold text-white">{typeof val === 'number' ? val.toLocaleString() : val}</div>
                          <div className="text-xs text-white">{label}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-white mb-1">YouTube · click a column to sort · views-based (watch-hours/CCV aren’t public for uploads).</p>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-white border-b border-white/10">
                          <th className="text-left py-2 font-medium">Creator</th>
                          {[['videos', 'Videos'], ['views', 'Total Views'], ['avgViews', 'Avg Views'], ['likes', 'Likes'], ['comments', 'Comments'], ['engagement', 'Engagement']].map(([k, lbl]) => (
                            <th key={k} onClick={() => toggleVcSort(k)}
                              className={`py-2 font-medium cursor-pointer select-none text-right hover:text-sc-pink ${vcSort.key === k ? 'text-sc-pink' : ''}`}
                            >{lbl}{vcArrow(k)}</th>
                          ))}
                          <th onClick={() => toggleVcSort('region')} className={`text-left pl-3 py-2 font-medium cursor-pointer select-none hover:text-sc-pink ${vcSort.key === 'region' ? 'text-sc-pink' : ''}`}>Region{vcArrow('region')}</th>
                          <th className="text-right py-2 font-medium">Latest</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vcSorted.map((c) => (
                          <tr key={c.channelId} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-2 font-medium text-white">
                              <a href={`https://www.youtube.com/channel/${c.channelId}`} target="_blank" rel="noopener noreferrer" className="hover:text-sc-pink hover:underline">{c.name}</a>
                              {c.onPlatform && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 bg-sc-pink/15 text-sc-pink rounded align-middle">on platform</span>}
                            </td>
                            <td className="py-2 text-right text-white">{c.videos}</td>
                            <td className="py-2 text-right font-semibold text-white">{c.views.toLocaleString()}</td>
                            <td className="py-2 text-right text-white">{c.avgViews.toLocaleString()}</td>
                            <td className="py-2 text-right text-white">{c.likes.toLocaleString()}</td>
                            <td className="py-2 text-right text-white">{c.comments.toLocaleString()}</td>
                            <td className="py-2 text-right text-white">{c.engagement}%</td>
                            <td className="py-2 text-left pl-3"><span className="px-2 py-0.5 bg-white/10 rounded text-xs text-white">{c.region}</span></td>
                            <td className="py-2 text-right text-white text-xs">{fmtDay(c.lastPublished)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              ) : gameContent.length === 0 ? (
                <div className="text-center py-8">
                  <Gamepad2 className="w-12 h-12 mx-auto text-white/20 mb-2" />
                  <p className="text-white">No content linked to this game yet.</p>
                  <p className="text-sm text-white mt-1">Click "Link Content" to scan and link videos/streams.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {gameContent.map((content) => (
                    <div key={content.id} className="flex items-center gap-3 p-3 bg-white/5 border border-white rounded-lg hover:bg-white/8 transition-colors">
                      {/* Thumbnail */}
                      {content.thumbnailUrl ? (
                        <img
                          src={getFullThumbnailUrl(content.thumbnailUrl)}
                          alt=""
                          className="w-24 h-14 object-cover rounded"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className={`w-24 h-14 bg-white/10 rounded items-center justify-center ${content.thumbnailUrl ? 'hidden' : 'flex'}`}>
                        <Gamepad2 className="w-6 h-6 text-white" />
                      </div>

                      {/* Content Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm truncate text-white" title={content.title}>{content.title}</h4>
                        <div className="flex items-center gap-2 text-xs text-white mt-1">
                          <span className={`px-1.5 py-0.5 rounded ${content.platform === 'youtube' ? 'bg-red-500/15 text-red-400' : content.platform === 'twitch' ? 'bg-purple-500/15 text-purple-300' : content.platform === 'kick' ? 'bg-[#00e70a]/15 text-[#00e70a]' : 'bg-white/10 text-white'}`}>
                            {({ youtube: 'YouTube', twitch: 'Twitch', kick: 'Kick', tiktok: 'TikTok', instagram: 'Instagram', twitter: 'Twitter' }[content.platform] || content.platform)}
                          </span>
                          {content.Creator && <span>{content.Creator.name}</span>}
                          {content.postDate && <span>{new Date(content.postDate).toLocaleDateString()}</span>}
                        </div>
                      </div>

                      {/* Views & Link */}
                      <div className="flex items-center gap-3 text-sm">
                        <div className="flex items-center gap-1 text-white">
                          <Eye className="w-4 h-4" />
                          <span>Views: {formatViews(content.views)}</span>
                          {isYouTubeDataStale(content) && (
                            <span className="text-amber-500" title="YouTube data >30 days old — refresh to update">&#9888;</span>
                          )}
                        </div>
                        {content.contentUrl && (
                          <a
                            href={content.contentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sc-pink hover:text-sc-pink/80"
                            onClick={e => e.stopPropagation()}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Games;
